import { Observable } from "rxjs/internal/Observable";
import { catchError, map } from "rxjs/operators";
import { API_URL } from "../../enums/api.enums";
import HttpClient from "../http-client";
import Utils from "../../utils/base-utils";
import {
  INotification,
  ICreateNotificationRequest,
  IUpdateNotificationRequest,
  INotificationStats,
  INotificationTemplate,
  ICreateTemplateRequest,
  IUserDeviceToken,
  ISendNotificationResponse,
  INotificationListResponse,
  IDeviceTokenStats,
  ITestNotificationRequest
} from "../../common/notification.interface";

export default class NotificationApi {
  static apiURL = API_URL;

  // Lấy danh sách tất cả notification với pagination
  static getAllNotifications(params?: any): Observable<INotificationListResponse> {
    const cleanParams = params ? Utils.getRidOfUnusedProperties({ ...params }) : {};
    const queryParam = Object.keys(cleanParams).length > 0 ? Utils.parseObjectToQueryParameter(cleanParams) : '';
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.GET_ALL_NOTIFICATIONS}${queryParam}`;
    console.log('API URL:', api);
    return HttpClient.get(api).pipe(
      map((res) => {
        console.log('Raw API response:', res);
        return (res as INotificationListResponse) || { data: [], pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 20 } };
      }),
      catchError((error) => {
        console.error('API Error:', error);
        throw error;
      })
    );
  }

  // Tạo notification mới
  static createNotification(data: ICreateNotificationRequest): Observable<INotification> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.CREATE_NOTIFICATION}`;
    return HttpClient.post(api, data).pipe(
      map(
        (res) => (res as INotification) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Cập nhật notification
  static updateNotification(id: string, data: IUpdateNotificationRequest): Observable<INotification> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.UPDATE_NOTIFICATION}/${id}`;
    return HttpClient.put(api, data).pipe(
      map(
        (res) => (res as INotification) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Xóa notification
  static deleteNotification(id: string): Observable<any> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.DELETE_NOTIFICATION}/${id}`;
    return HttpClient.delete(api).pipe(
      map(
        (res) => (res as any) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Gửi notification
  static sendNotification(id: string): Observable<ISendNotificationResponse> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.SEND_NOTIFICATION}/${id}`;
    return HttpClient.post(api, {}).pipe(
      map(
        (res) => (res as ISendNotificationResponse) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Gửi test notification
  static sendTestNotification(data: ITestNotificationRequest): Observable<ISendNotificationResponse> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.SEND_TEST_NOTIFICATION}`;
    return HttpClient.post(api, data).pipe(
      map(
        (res) => (res as ISendNotificationResponse) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Lấy thống kê notification
  static getNotificationStats(params?: any): Observable<INotificationStats> {
    const queryParam = params ? Utils.parseObjectToQueryParameter(params) : '';
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.GET_NOTIFICATION_STATS}${queryParam}`;
    return HttpClient.get(api).pipe(
      map(
        (res) => (res as INotificationStats) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Lấy danh sách template
  static getNotificationTemplates(): Observable<INotificationTemplate[]> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.GET_NOTIFICATION_TEMPLATES}`;
    return HttpClient.get(api).pipe(
      map(
        (res) => (res as INotificationTemplate[]) || [],
        catchError((error) => new Observable())
      )
    );
  }

  // Tạo template mới
  static createNotificationTemplate(data: ICreateTemplateRequest): Observable<INotificationTemplate> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.CREATE_NOTIFICATION_TEMPLATE}`;
    return HttpClient.post(api, data).pipe(
      map(
        (res) => (res as INotificationTemplate) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Cập nhật template
  static updateNotificationTemplate(id: string, data: Partial<ICreateTemplateRequest>): Observable<INotificationTemplate> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.UPDATE_NOTIFICATION_TEMPLATE}/${id}`;
    return HttpClient.put(api, data).pipe(
      map(
        (res) => (res as INotificationTemplate) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Xóa template
  static deleteNotificationTemplate(id: string): Observable<any> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.DELETE_NOTIFICATION_TEMPLATE}/${id}`;
    return HttpClient.delete(api).pipe(
      map(
        (res) => (res as any) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Lấy danh sách device token của user
  static getUserDeviceTokens(params?: any): Observable<IUserDeviceToken[]> {
    const queryParam = params ? Utils.parseObjectToQueryParameter(params) : '';
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.GET_USER_DEVICE_TOKENS}${queryParam}`;
    return HttpClient.get(api).pipe(
      map(
        (res) => (res as IUserDeviceToken[]) || [],
        catchError((error) => new Observable())
      )
    );
  }

  // Lấy chi tiết notification
  static getNotificationDetail(id: string): Observable<INotification> {
    const api = `${NotificationApi.apiURL.HOST}/${NotificationApi.apiURL.GET_ALL_NOTIFICATIONS}/${id}`;
    return HttpClient.get(api).pipe(
      map(
        (res) => (res as INotification) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Device Token Management
  // Lấy danh sách device tokens (admin)
  static getDeviceTokens(params?: any): Observable<IUserDeviceToken[]> {
    const queryParam = params ? Utils.parseObjectToQueryParameter(params) : '';
    const api = `${NotificationApi.apiURL.HOST}/device-tokens${queryParam}`;
    return HttpClient.get(api).pipe(
      map(
        (res) => (res as IUserDeviceToken[]) || [],
        catchError((error) => new Observable())
      )
    );
  }

  // Lấy thống kê device tokens
  static getDeviceTokenStats(): Observable<IDeviceTokenStats> {
    const api = `${NotificationApi.apiURL.HOST}/device-tokens/stats`;
    return HttpClient.get(api).pipe(
      map(
        (res) => (res as IDeviceTokenStats) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Test device token
  static testDeviceToken(tokenId: string): Observable<any> {
    const api = `${NotificationApi.apiURL.HOST}/device-tokens/test/${tokenId}`;
    return HttpClient.post(api, {}).pipe(
      map(
        (res) => (res as any) || null,
        catchError((error) => new Observable())
      )
    );
  }

  // Cleanup inactive tokens
  static cleanupInactiveTokens(days: number = 30): Observable<any> {
    const api = `${NotificationApi.apiURL.HOST}/device-tokens/cleanup?days=${days}`;
    return HttpClient.post(api, {}).pipe(
      map(
        (res) => (res as any) || null,
        catchError((error) => new Observable())
      )
    );
  }
}
